import { AlertCircle, Check, Link as LinkIcon } from "lucide-react";
import React from "react";
import { useCodiffLinks } from "../hooks/useCodiffLinks";
import { copyMrLink } from "../utils";
import { Tooltip } from "./Tooltip";

// How long the "copied"/"failed" feedback state stays visible (ms).
const FEEDBACK_DURATION_MS = 1500;

// Button that copies an MR as a rich-text hyperlink to the clipboard.
// Pastes into Google Docs (and similar editors) as a clickable link whose
// display text is "repo #iid: title".
export const CopyLinkButton = ({ mr, className = "" }) => {
	// "idle" | "copied" | "error"
	const [status, setStatus] = React.useState("idle");
	const timeoutRef = React.useRef(null);
	const [useCodiff] = useCodiffLinks();

	// Clear any pending feedback timer on unmount to avoid setting state on an
	// unmounted component.
	React.useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const handleClick = async (e) => {
		// Avoid triggering drag/navigation on the parent card.
		e.preventDefault();
		e.stopPropagation();

		const ok = await copyMrLink(mr, useCodiff);
		setStatus(ok ? "copied" : "error");

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = setTimeout(() => {
			setStatus("idle");
			timeoutRef.current = null;
		}, FEEDBACK_DURATION_MS);
	};

	const tooltip =
		status === "copied"
			? "Copied!"
			: status === "error"
				? "Copy failed"
				: "Copy link";

	return (
		<Tooltip content={tooltip}>
			<button
				type="button"
				onClick={handleClick}
				draggable="false"
				aria-label="Copy link to merge request"
				className={`p-1 -mx-1 rounded hover:bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:text-[var(--color-link)] transition-colors ${className}`}
			>
				{status === "copied" ? (
					<Check className="w-4 h-4 text-green-500" />
				) : status === "error" ? (
					<AlertCircle className="w-4 h-4 text-red-500" />
				) : (
					<LinkIcon className="w-4 h-4" />
				)}
			</button>
		</Tooltip>
	);
};

export default CopyLinkButton;
